import React, { useState } from 'react';

const TYPE_META = {
  item: {
    label: 'Items',
    singular: 'Item',
    icon: 'inventory_2',
    accent: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/20',
    brief: (d) => `${d.length} × ${d.width} × ${d.height} m · ${d.maxStackingWeight} kg · stack ×${d.maxStackingCount}`,
    defaultData: { length: 0, width: 0, height: 0, maxStackingWeight: 10, maxStackingCount: 5, orientationRestricted: false, refrigerated: false },
  },
  container: {
    label: 'Containers',
    singular: 'Container',
    icon: 'deployed_code',
    accent: 'bg-primary/15 text-primary border-primary/20',
    brief: (d) => `${d.length} × ${d.width} × ${d.height} m · ${d.weight} kg max · $${d.cost}`,
    defaultData: { length: 5.9, width: 2.35, height: 2.39, weight: 21800, maxCapacity: { weight: 100, volume: 100 }, cost: 0, refrigerated: false },
  },
  pallet: {
    label: 'Pallets',
    singular: 'Pallet',
    icon: 'layers',
    accent: 'bg-violet-500/15 text-violet-300 border-violet-500/20',
    brief: (d) => `${d.length} × ${d.width} × ${d.height} m · tare ${d.weight} kg · max load ${d.maxWeightAllowed} kg`,
    defaultData: { length: 1.2, width: 0.8, height: 0.15, weight: 25, maxWeightAllowed: 1500, sameItemOnly: false, isDoubleStackingAllowed: false },
  },
};

const inputCls = 'w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-sm text-white placeholder:text-slate-600 outline-none transition focus:border-primary/50 focus:bg-white/10';

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${value ? 'bg-primary' : 'bg-white/20'}`}
    >
      <span className={`inline-block h-4 w-4 mt-0.5 transform rounded-full bg-white shadow transition ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

// ── Add forms ─────────────────────────────────────────────────────────────────

function ItemAddForm({ onSave, onCancel, saving }) {
  const [d, setD] = useState({ name: '', ...TYPE_META.item.defaultData });
  const set = (k) => (e) => setD((p) => ({ ...p, [k]: typeof p[k] === 'boolean' ? !p[k] : (parseFloat(e.target.value) || 0) }));
  const valid = d.name.trim() && d.length > 0 && d.width > 0 && d.height > 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5 space-y-4">
      <p className="text-sm font-bold text-white">New Item</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold text-slate-400">Name *</label>
          <input className={inputCls} placeholder="e.g. Carton-A" value={d.name} onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))} />
        </div>
        {[['length','Length (m)'],['width','Width (m)'],['height','Height (m)']].map(([k, lbl]) => (
          <div key={k}>
            <label className="mb-1 block text-xs font-semibold text-slate-400">{lbl}</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={d[k]} onChange={set(k)} />
          </div>
        ))}
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Weight per unit (kg)</label>
          <input className={inputCls} type="number" step="0.1" min="0" value={d.maxStackingWeight} onChange={(e) => setD((p) => ({ ...p, maxStackingWeight: parseFloat(e.target.value) || 0 }))} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Max stacking count</label>
          <input className={inputCls} type="number" min="1" value={d.maxStackingCount} onChange={(e) => setD((p) => ({ ...p, maxStackingCount: parseInt(e.target.value) || 1 }))} />
        </div>
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Toggle value={d.orientationRestricted} onChange={(v) => setD((p) => ({ ...p, orientationRestricted: v }))} />
          Fixed orientation
        </label>
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Toggle value={d.refrigerated} onChange={(v) => setD((p) => ({ ...p, refrigerated: v }))} />
          Refrigerated
        </label>
      </div>
      <div className="flex gap-3 pt-1">
        <button disabled={!valid || saving} onClick={() => onSave(d.name, d)} className="rounded-xl bg-primary/80 px-5 py-2 text-sm font-bold text-white hover:bg-primary transition disabled:opacity-40">
          {saving ? 'Saving…' : 'Save Item'}
        </button>
        <button onClick={onCancel} className="rounded-xl border border-white/10 px-5 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
      </div>
    </div>
  );
}

function ContainerAddForm({ onSave, onCancel, saving }) {
  const [d, setD] = useState({ name: '', ...TYPE_META.container.defaultData });
  const set = (k) => (e) => setD((p) => ({ ...p, [k]: parseFloat(e.target.value) || 0 }));
  const setN = (k) => (e) => setD((p) => ({ ...p, maxCapacity: { ...p.maxCapacity, [k]: parseFloat(e.target.value) || 0 } }));
  const valid = d.name.trim() && d.length > 0 && d.width > 0 && d.height > 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5 space-y-4">
      <p className="text-sm font-bold text-white">New Container</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold text-slate-400">Name *</label>
          <input className={inputCls} placeholder="e.g. 40ft HC" value={d.name} onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))} />
        </div>
        {[['length','Length (m)'],['width','Width (m)'],['height','Height (m)']].map(([k, lbl]) => (
          <div key={k}>
            <label className="mb-1 block text-xs font-semibold text-slate-400">{lbl}</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={d[k]} onChange={set(k)} />
          </div>
        ))}
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Max payload (kg)</label>
          <input className={inputCls} type="number" min="0" value={d.weight} onChange={set('weight')} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Cost per container (USD)</label>
          <input className={inputCls} type="number" min="0" value={d.cost} onChange={set('cost')} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Max weight utilisation %</label>
          <input className={inputCls} type="number" min="1" max="100" value={d.maxCapacity.weight} onChange={setN('weight')} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Max volume utilisation %</label>
          <input className={inputCls} type="number" min="1" max="100" value={d.maxCapacity.volume} onChange={setN('volume')} />
        </div>
      </div>
      <div className="flex gap-3 pt-1">
        <button disabled={!valid || saving} onClick={() => onSave(d.name, d)} className="rounded-xl bg-primary/80 px-5 py-2 text-sm font-bold text-white hover:bg-primary transition disabled:opacity-40">
          {saving ? 'Saving…' : 'Save Container'}
        </button>
        <button onClick={onCancel} className="rounded-xl border border-white/10 px-5 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
      </div>
    </div>
  );
}

function PalletAddForm({ onSave, onCancel, saving }) {
  const [d, setD] = useState({ name: '', ...TYPE_META.pallet.defaultData });
  const set = (k) => (e) => setD((p) => ({ ...p, [k]: parseFloat(e.target.value) || 0 }));
  const valid = d.name.trim() && d.length > 0 && d.width > 0 && d.height > 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5 space-y-4">
      <p className="text-sm font-bold text-white">New Pallet</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-semibold text-slate-400">Name *</label>
          <input className={inputCls} placeholder="e.g. EURO Pallet" value={d.name} onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))} />
        </div>
        {[['length','Length (m)'],['width','Width (m)'],['height','Height (m)']].map(([k, lbl]) => (
          <div key={k}>
            <label className="mb-1 block text-xs font-semibold text-slate-400">{lbl}</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={d[k]} onChange={set(k)} />
          </div>
        ))}
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Tare weight (kg)</label>
          <input className={inputCls} type="number" min="0" value={d.weight} onChange={set('weight')} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Max load weight (kg)</label>
          <input className={inputCls} type="number" min="0" value={d.maxWeightAllowed} onChange={set('maxWeightAllowed')} />
        </div>
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Toggle value={d.sameItemOnly} onChange={(v) => setD((p) => ({ ...p, sameItemOnly: v }))} />
          Same item only
        </label>
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Toggle value={d.isDoubleStackingAllowed} onChange={(v) => setD((p) => ({ ...p, isDoubleStackingAllowed: v }))} />
          Double stacking allowed
        </label>
      </div>
      <div className="flex gap-3 pt-1">
        <button disabled={!valid || saving} onClick={() => onSave(d.name, d)} className="rounded-xl bg-primary/80 px-5 py-2 text-sm font-bold text-white hover:bg-primary transition disabled:opacity-40">
          {saving ? 'Saving…' : 'Save Pallet'}
        </button>
        <button onClick={onCancel} className="rounded-xl border border-white/10 px-5 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
      </div>
    </div>
  );
}

const ADD_FORMS = { item: ItemAddForm, container: ContainerAddForm, pallet: PalletAddForm };

// ── Asset card ────────────────────────────────────────────────────────────────

function AssetCard({ asset, meta, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(asset._id);
  };

  const created = new Date(asset.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-4 hover:bg-white/[0.06] transition">
      <div className="flex items-start gap-3 min-w-0">
        <span className={`mt-0.5 shrink-0 rounded px-2 py-0.5 font-mono text-xs font-bold border ${meta.accent}`}>
          {asset.assetId}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-white truncate">{asset.name}</p>
            {asset.isDefault && (
              <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold bg-white/8 text-slate-500">
                standard
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{meta.brief(asset.data)}</p>
          <p className="mt-1 text-[11px] text-slate-600">Added {created}</p>
        </div>
      </div>

      {confirm ? (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="rounded-lg bg-red-500/20 px-2.5 py-1 text-xs font-bold text-red-400 hover:bg-red-500/30 transition disabled:opacity-50"
          >
            {deleting ? '…' : 'Delete'}
          </button>
          <button onClick={() => setConfirm(false)} className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-slate-400 hover:text-white transition">
            Cancel
          </button>
        </div>
      ) : (
        <button onClick={() => setConfirm(true)} className="shrink-0 text-slate-600 hover:text-red-400 transition">
          <span className="material-symbols-outlined text-[16px]">delete</span>
        </button>
      )}
    </div>
  );
}

// ── Per-type section ──────────────────────────────────────────────────────────

function TypeSection({ type, list, onAdd, onDelete, query }) {
  const meta    = TYPE_META[type];
  const AddForm = ADD_FORMS[type];
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const filtered = list.filter(
    (a) =>
      a.name.toLowerCase().includes(query.toLowerCase()) ||
      a.assetId.toLowerCase().includes(query.toLowerCase())
  );

  const handleSave = async (name, data) => {
    setSaving(true);
    try { await onAdd(type, name, data); setAdding(false); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-slate-400" style={{ fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
          <h3 className="text-base font-bold text-white">{meta.label}</h3>
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-bold text-slate-400">{list.length}</span>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-white/10 hover:text-white transition"
        >
          <span className="material-symbols-outlined text-[14px]">{adding ? 'remove' : 'add'}</span>
          Add {meta.singular}
        </button>
      </div>

      {adding && (
        <div className="mb-4">
          <AddForm onSave={handleSave} onCancel={() => setAdding(false)} saving={saving} />
        </div>
      )}

      {filtered.length === 0 && !adding && (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] px-5 py-8 text-center">
          <span className="material-symbols-outlined text-[28px] text-slate-700" style={{ fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
          <p className="mt-2 text-sm text-slate-500">{query ? `No ${meta.label.toLowerCase()} match "${query}"` : `No ${meta.label.toLowerCase()} yet.`}</p>
          {!query && <p className="mt-1 text-xs text-slate-600">Add one manually or run a simulation — assets save automatically.</p>}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((a) => (
          <AssetCard key={a._id} asset={a} meta={meta} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

// ── Main AssetsTab ────────────────────────────────────────────────────────────

export function AssetsTab({ assetHook }) {
  const { assets, loading, add, remove } = assetHook;
  const [query, setQuery] = useState('');

  const total = assets.item.length + assets.container.length + assets.pallet.length;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-black text-white">Assets</h1>
          <p className="mt-1 text-sm text-slate-400">
            {total > 0
              ? `${assets.item.length} items · ${assets.container.length} containers · ${assets.pallet.length} pallets — reusable across simulations.`
              : 'Your reusable items, containers, and pallets. Auto-saved after each simulation.'}
          </p>
        </div>
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-600">search</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or ID…"
            className="rounded-xl border border-white/10 bg-white/5 pl-9 pr-4 py-2 text-sm text-white placeholder:text-slate-600 outline-none focus:border-primary/50 transition w-56"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-16 rounded-xl border border-white/5 bg-white/[0.02] animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-10">
          <TypeSection type="item"      list={assets.item}      onAdd={add} onDelete={remove} query={query} />
          <TypeSection type="container" list={assets.container} onAdd={add} onDelete={remove} query={query} />
          <TypeSection type="pallet"    list={assets.pallet}    onAdd={add} onDelete={remove} query={query} />
        </div>
      )}
    </div>
  );
}
